import { useCallback, useEffect, useState } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { Monitor } from 'lucide-react';
import { Button } from '@/components/ui';
import { GradientDots } from '@/components/ui/GradientDots';
import { OperatorWordmark } from '@/components/auth/OperatorWordmark';
import { GlowInput } from '@/components/auth/GlowInput';
import { useAuth } from '../../store/auth-context';
import { deviceApi, ApiError, type DeviceRequestInfo } from '../../services/api';

type PageState =
  | 'enter-code' // no/invalid code — manual entry
  | 'loading'
  | 'review' // show device details + Approve / Deny
  | 'approving'
  | 'approved'
  | 'denied'
  | 'not-found'
  | 'error';

/**
 * Cross-device sign-in — the APPROVING device's page (the phone), at /device.
 *
 * Reached by scanning the QR code on the consuming device
 * (?user_code=XXXX-XXXX). Unauthenticated visitors bounce through /signin
 * first; the deep link (path + query) survives the round-trip for both
 * password and Okta sign-ins.
 *
 * Security posture (cross-device-flow BCP): the details of the requesting
 * device are shown, approval is always an explicit click, and denial is one
 * tap. Only the short user_code ever reaches this page — tokens flow solely
 * to the polling device holding the secret device_code.
 *
 * Federated (Okta) approvers get `status: 'redirect'` back from approve and
 * complete via a silent Hosted-UI round-trip; the callback returns here with
 * ?approved=1 (or ?error=…).
 */
export default function DeviceApprovePage() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const urlCode = searchParams.get('user_code') ?? '';
  const cameBackApproved = searchParams.get('approved') === '1';
  const callbackError = searchParams.get('error');

  const [state, setState] = useState<PageState>(() => {
    if (cameBackApproved) return 'approved';
    if (callbackError) return 'error';
    return urlCode ? 'loading' : 'enter-code';
  });
  const [code, setCode] = useState(urlCode);
  const [info, setInfo] = useState<DeviceRequestInfo | null>(null);
  const [formError, setFormError] = useState('');

  const loadInfo = useCallback(async (userCode: string) => {
    setState('loading');
    try {
      const loaded = await deviceApi.info(userCode);
      if (loaded.status !== 'pending') {
        setState('not-found');
        return;
      }
      setInfo(loaded);
      setState('review');
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setState('not-found');
      } else {
        setState('error');
      }
    }
  }, []);

  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    if (cameBackApproved || callbackError) return;
    if (urlCode) loadInfo(urlCode);
  }, [isLoading, isAuthenticated, urlCode, cameBackApproved, callbackError, loadInfo]);

  if (!isLoading && !isAuthenticated) {
    // Sign in first; SignIn preserves pathname + search so the code survives.
    return <Navigate to="/signin" replace state={{ from: location }} />;
  }

  const handleCodeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) {
      setFormError('Please enter the code shown on your other device.');
      return;
    }
    setFormError('');
    loadInfo(trimmed);
  };

  const handleDecision = async (action: 'approve' | 'deny') => {
    if (!info) return;
    setState('approving');
    try {
      const result = await deviceApi.approve(info.userCode, action);
      if (result.status === 'redirect') {
        // Okta approver: silent Hosted-UI round-trip finishes the approval.
        window.location.href = result.authorizeUrl;
        return;
      }
      setState(result.status === 'approved' ? 'approved' : 'denied');
    } catch (err) {
      if (err instanceof ApiError && (err.status === 404 || err.status === 409)) {
        setState('not-found');
      } else {
        setState('error');
      }
    }
  };

  return (
    <div
      className="relative min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-12"
      style={{ backgroundColor: 'hsl(var(--auth-bg))' }}
    >
      <GradientDots />

      <div className="relative z-10 w-full max-w-xl rounded-[32px] border border-foreground/15 bg-card/95 dark:bg-card/15 p-6 shadow-[0_80px_160px_-30px_rgba(0,0,0,0.8),0_40px_80px_-20px_rgba(0,0,0,0.6)] backdrop-blur-3xl sm:p-12">
        <div className="flex flex-col items-center text-center">
          <OperatorWordmark size="xl" />
          <p className="mt-6 text-[10px] tracking-[0.15em] sm:text-xs sm:tracking-[0.25em] uppercase text-muted-foreground">
            Approve device sign-in
          </p>
        </div>

        <div className="mt-10 min-h-[220px]">
          {(isLoading || state === 'loading') && (
            <p className="text-center text-sm text-muted-foreground">Loading…</p>
          )}

          {state === 'enter-code' && !isLoading && (
            <form onSubmit={handleCodeSubmit} className="space-y-5" noValidate>
              <p className="text-center text-sm text-muted-foreground">
                Enter the code shown on the device you're signing in.
              </p>
              <GlowInput
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="XXXX-XXXX"
                aria-label="Device code"
                className="text-center font-mono uppercase tracking-[0.2em]"
              />
              {formError && (
                <p role="alert" className="text-center text-sm text-[hsl(335_85%_70%)]">
                  {formError}
                </p>
              )}
              <Button type="submit" className="w-full" size="lg">
                Continue
              </Button>
            </form>
          )}

          {state === 'review' && info && (
            <div className="space-y-6">
              <div className="rounded-lg border border-border bg-muted/40 px-5 py-4">
                <div className="flex items-center gap-3">
                  <Monitor className="h-5 w-5 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 text-left text-sm">
                    <p className="font-medium text-foreground">
                      A device wants to sign in to your account
                    </p>
                    <p className="mt-1 truncate text-muted-foreground">
                      {describeDevice(info)}
                    </p>
                  </div>
                </div>
                <p className="mt-3 text-center font-mono text-2xl font-semibold tracking-[0.25em] text-foreground">
                  {info.userCode}
                </p>
              </div>

              <p className="text-center text-xs text-muted-foreground">
                Only approve if this code matches the one on <em>your</em> screen.
                If you didn't start this, deny it.
              </p>

              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  className="flex-1"
                  size="lg"
                  onClick={() => handleDecision('deny')}
                >
                  Deny
                </Button>
                <Button className="flex-1" size="lg" onClick={() => handleDecision('approve')}>
                  Approve
                </Button>
              </div>
            </div>
          )}

          {state === 'approving' && (
            <p className="text-center text-sm text-muted-foreground">Approving…</p>
          )}

          {state === 'approved' && (
            <div className="space-y-3 text-center">
              <p className="text-sm font-medium text-foreground">Device approved.</p>
              <p className="text-sm text-muted-foreground">
                Return to your other device — it will finish signing in within a few
                seconds. You can close this page.
              </p>
            </div>
          )}

          {state === 'denied' && (
            <div className="space-y-3 text-center">
              <p className="text-sm font-medium text-foreground">Request denied.</p>
              <p className="text-sm text-muted-foreground">
                The other device will not be signed in. You can close this page.
              </p>
            </div>
          )}

          {state === 'not-found' && (
            <div className="space-y-5 text-center">
              <p className="text-sm text-muted-foreground">
                That code isn't valid anymore — it may have expired or already been
                used. Ask the device for a fresh code and try again.
              </p>
              <Button onClick={() => { setCode(''); setInfo(null); setState('enter-code'); }}>
                Enter a new code
              </Button>
            </div>
          )}

          {state === 'error' && (
            <div className="space-y-5 text-center">
              <p className="text-sm text-muted-foreground">
                {callbackError === 'code_expired'
                  ? 'The code expired before the approval completed. Ask the device for a fresh code.'
                  : 'Something went wrong. Please try again.'}
              </p>
              <Button onClick={() => { setCode(''); setInfo(null); setState('enter-code'); }}>
                Enter a new code
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Short human-readable description of the requesting client. */
function describeDevice(info: DeviceRequestInfo): string {
  const parts: string[] = [];
  if (info.client.userAgent) parts.push(summarizeUserAgent(info.client.userAgent));
  if (info.client.ip) parts.push(`IP ${info.client.ip}`);
  return parts.length > 0 ? parts.join(' · ') : 'Unknown device';
}

function summarizeUserAgent(ua: string): string {
  if (/tesla/i.test(ua)) return 'Tesla browser';
  if (/smart-?tv|tizen|webos/i.test(ua)) return 'Smart TV';
  if (/mobile|iphone|android/i.test(ua)) return 'Mobile browser';
  const browser = ua.match(/(firefox|edg|chrome|safari)/i)?.[1];
  return browser ? `${browser[0].toUpperCase()}${browser.slice(1).toLowerCase()} browser` : 'Browser';
}
